import type { CatalogUerBaseCostHistoryData, CatalogUerBaseCostHistoryDataFilterInput } from 'gql/graphql';
import { UerBaseCostChangeType } from 'gql/graphql';
import { formatNumberToRu } from 'utils';

import type { RowClassRules } from 'ag-grid-community';
import type { NestedFieldPaths } from 'ag-grid-community/dist/types/core/entities/colDef';

export type TFilterType = 'dateFilter' | 'typeFilter' | 'columnFilters';
export type TFilter = {
    dateFilter: [Date?, Date?] | null;
    typeFilter: UerBaseCostChangeType | null;
    columnFilters: Record<string, any[] | null>;
};

export const historyStylesRules: RowClassRules = {
    headerRow: ({ data }: { data: CatalogUerBaseCostHistoryData }) => Boolean(data?.isHeadLine),
    addedRowClass: ({ data }: { data: CatalogUerBaseCostHistoryData }) =>
        Boolean(data?.uerBaseCostChangeType === UerBaseCostChangeType.New),
    updatedRowClass: ({ data }: { data: CatalogUerBaseCostHistoryData }) =>
        Boolean(data?.uerBaseCostChangeType === UerBaseCostChangeType.Updated),
    deletedRowClass: ({ data }: { data: CatalogUerBaseCostHistoryData }) =>
        Boolean(data?.uerBaseCostChangeType === UerBaseCostChangeType.Deleted),
};

export const numberFormatter = ({ value }: { value: number }) => formatNumberToRu(value, 2);

export const getHistoryFilterValue = (filterValue: TFilter) => {
    const columnFilters = Object.entries(filterValue?.columnFilters ?? {}).reduce(
        (acc: Array<Record<string, any>>, [key, valueArr]) => {
            if (valueArr?.length) {
                const [left, right] = key.split('.');

                let resultValue = {
                    [left]: {
                        [right]: { in: valueArr },
                    },
                } as any;

                if (right === 'hasWorkScopesHistory') {
                    resultValue = {
                        [left]: {
                            [right]: {
                                eq: true,
                            },
                        },
                    };
                }

                if (right === 'groupFullName') {
                    resultValue = {
                        [left]: {
                            or: valueArr.map((v) => ({
                                and: [
                                    {
                                        groupCode: { eq: v.groupCode },
                                    },
                                    {
                                        groupFullName: { eq: v.groupName },
                                    },
                                ],
                            })),
                        },
                    };
                }

                acc.push(resultValue);
            }

            return acc;
        },
        [],
    );

    return {
        and: [
            {
                updateUerDate: {
                    gte: filterValue?.dateFilter?.[0]?.toISOString(),
                },
            },
            {
                updateUerDate: {
                    lte: filterValue?.dateFilter?.[1]?.toISOString(),
                },
            },
            ...(filterValue?.typeFilter
                ? [
                      {
                          or: [
                              {
                                  uerBaseCostChangeType: {
                                      eq: filterValue.typeFilter,
                                  },
                              },
                          ],
                      },
                  ]
                : []),
            ...columnFilters,
        ],
    } as CatalogUerBaseCostHistoryDataFilterInput;
};

export const groupFullNameFilterWhereGetter = (searchStr: string) => ({
    or: [
        {
            value: {
                groupCode: {
                    containsi: searchStr,
                },
            },
        },
        {
            value: {
                groupName: {
                    containsi: searchStr,
                },
            },
        },
    ],
});
type THistoryColumnKeys =
    | 'groupFullName'
    | 'code'
    | 'name'
    | 'chapter'
    | 'uerClassifier'
    | 'hasWorkScopesHistory'
    | 'justification';
export const historyFilterColumns: {
    [key in THistoryColumnKeys]: NestedFieldPaths<CatalogUerBaseCostHistoryData, any, []>;
} = {
    groupFullName: 'catalogUERBaseCostChange.groupFullName',
    code: 'catalogUERBaseCostChange.code',
    name: 'catalogUERBaseCostChange.name',
    chapter: 'catalogUERBaseCostChange.chapter',
    uerClassifier: 'catalogUERBaseCostChange.uerClassifier',
    hasWorkScopesHistory: 'catalogUERBaseCostChange.hasWorkScopesHistory',
    justification: 'catalogUERBaseCostChange.justification',
};
export const historyFilterColumnsFieldNames = Object.values(historyFilterColumns);
